import { useMemo, useState } from "react";
import CircularProgress from "../CircularProgress";
import ConfirmDialog from "./ConfirmDialog";
import VideoList from "./VideoList";
import config from "../config";
import { deleteVideos, reprocessVideos } from "../api";
import { groupVideosByFile, toggleValue, truncate } from "../utils/parsers";

export default function ProcessingTab({
  progress,
  remainingMinutes,
  videos,
  counts,
  countStatus,
  countError,
  dayList,
  selectedDate,
  onDateChange,
  onGoToUpload,
  showToast,
}) {
  // One card per file; a day video's two jobs share it.
  const cards = useMemo(() => groupVideosByFile(videos), [videos]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [reprocessTarget, setReprocessTarget] = useState(null);
  const [reprocessing, setReprocessing] = useState(null);
  const isEmpty = cards.length === 0;

  const chosenCards = cards.filter((card) => selectedKeys.includes(card.key));

  const handleStop = async () => {
    const ids = chosenCards.flatMap((card) => card.ids);
    setStopping(true);
    try {
      await deleteVideos(ids);
      showToast(
        "success",
        `Stopped ${chosenCards.length} video(s). Find them under the day list to process again.`,
      );
      setSelectedKeys([]);
      dayList?.reload?.();
    } catch (error) {
      showToast("error", `Could not stop videos: ${error.message}`);
    } finally {
      setStopping(false);
      setShowConfirm(false);
    }
  };

  const handleReprocess = async () => {
    const group = reprocessTarget;
    if (!group) return;
    setReprocessing(group.key);
    try {
      const result = await reprocessVideos(group.ids);
      const requeued = Array.isArray(result?.requeued) ? result.requeued : [];
      const skipped = Array.isArray(result?.skipped) ? result.skipped : [];
      const reasons = [...new Set(skipped.map((s) => s.reason))].join("; ");
      if (requeued.length > 0) {
        showToast(
          skipped.length ? "warning" : "success",
          `${group.name} queued for processing again` +
            (skipped.length ? ` (${skipped.length} job(s) skipped: ${reasons})` : "") +
            ".",
        );
      } else {
        showToast("error", `${group.name} was not queued: ${reasons || "nothing to do"}.`);
      }
      dayList?.reload?.();
    } catch (error) {
      showToast("error", `Could not queue ${group.name}: ${error.message}`);
    } finally {
      setReprocessing(null);
      setReprocessTarget(null);
    }
  };

  const tiles = [
    ["UNIQUE VIDEOS", counts.uniqueFiles],
    ["LINEAR", counts.linear],
    ["FIXED", counts.fixed],
    ["LANE", counts.lane],
    ["NIGHT", counts.night],
    ["QUEUED VIDEOS", counts.pending],
    ["PROCESSED VIDEOS  (Linear + Fixed + Lane + Night) ", counts.processed],
  ];

  return (
    <div className="page-container">
      <div className="split-screen">
        <div className="progress-container">
          {!isEmpty && (
            <>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress ?? 0}%` }}
                ></div>
              </div>
              <p className="progress-text">{progress ?? 0}%</p>
              <p className="progress-text">
                Estimated Time Taken for Videos to Complete Processing :{" "}
                {remainingMinutes} Mins
              </p>
            </>
          )}

          <div
            className="video-grid"
            style={{
              height: isEmpty ? undefined : "45vh",
              padding: isEmpty ? 0 : undefined,
            }}
          >
            {isEmpty && (
              <div className="no-videos-text">
                No videos available. Click on{" "}
                <span
                  className="upload-link"
                  style={{ cursor: "pointer" }}
                  onClick={onGoToUpload}
                >
                  Upload Videos
                </span>{" "}
                tab to add videos.
              </div>
            )}

            {!isEmpty && (
              <div className="video-grid">
                {cards.map((card) => {
                  const isSelected = selectedKeys.includes(card.key);
                  return (
                    <div
                      key={card.key}
                      onClick={() =>
                        setSelectedKeys((prev) => toggleValue(prev, card.key))
                      }
                      className={`video-card ${isSelected ? "selected" : ""}`}
                      title={card.name}
                    >
                      <div>{truncate(card.name, config.videoNameMaxLength)}</div>
                      <div className="card-jobs">
                        {card.jobs.map((job, index) => (
                          <span className="chip" key={job.id ?? index}>
                            {job.asset_type ?? "job"} {job.progress}%
                          </span>
                        ))}
                      </div>
                      <div className="progress-wrapper">
                        <CircularProgress value={card.progress} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {selectedKeys.length > 0 && (
            <button className="btn" onClick={() => setShowConfirm(true)}>
              STOP
            </button>
          )}
        </div>
      </div>

      <div className="date-row">
        <label className="no-videos-text" htmlFor="processed-date">
          Processed Video Details For Date:
        </label>
        <input
          id="processed-date"
          type="date"
          value={selectedDate || ""}
          onChange={(e) => onDateChange(e.target.value || null)}
        />
      </div>

      {countStatus === "loading" && (
        <p className="status-text">Loading counts...</p>
      )}
      {countStatus === "error" && (
        <p className="status-text error">Could not load counts: {countError}</p>
      )}

      <div className="row">
        {tiles.map(([label, value]) => (
          <div className="current-updates" key={label}>
            <div className="value">{value}</div>
            <div>{label}</div>
          </div>
        ))}
      </div>

      {dayList && (
        <VideoList
          groups={dayList.groups}
          status={dayList.status}
          error={dayList.error}
          date={selectedDate}
          busyKey={reprocessing}
          onReprocess={(group) => setReprocessTarget(group)}
        />
      )}

      {showConfirm && (
        <ConfirmDialog
          message={`Are you sure you want to stop ${selectedKeys.length} selected video(s)?`}
          busy={stopping}
          onCancel={() => setShowConfirm(false)}
          onConfirm={handleStop}
        />
      )}

      {reprocessTarget && (
        <ConfirmDialog
          title="Process again"
          message={`Process ${reprocessTarget.name} again? Its ${reprocessTarget.jobs.length} job(s) (${reprocessTarget.jobs.map((job) => job.asset_type ?? "job").join(", ")}) will run from the start.`}
          busy={reprocessing === reprocessTarget.key}
          confirmLabel="Process again"
          busyLabel="Queuing..."
          onCancel={() => setReprocessTarget(null)}
          onConfirm={handleReprocess}
        />
      )}
    </div>
  );
}
